<template>
  <el-form
    @submit.prevent="validateSubmit('safeBankForm')"
    :model="template"
    :rules="rules"
    label-position="top"
    ref="safeBankForm"
    novalidate
  >
    <input-section>
      <div class="flex flex-col md:flex-row justify-end">
        <div class="md:w-4/12 m-2">
          <horizontal-button
            :title="$t('actions.saveChanges')"
            isSuccess
            faIcon="save"
            @click.prevent="validateSubmit('safeBankForm')"
          />
        </div>

        <div class="md:w-4/12 m-2" v-if="enableClearForm">
          <horizontal-button
            :title="$t('actions.clearButton')"
            isDanger
            faIcon="trash"
            @click.prevent="clearForm('safeBankForm')"
          />
        </div>
      </div>
    </input-section>

    <!-- @submit errors -->
    <alert-badge
      isDanger
      :title="error.title"
      :body="error.body"
      @dismissed="
        () => {
          error.title = ''
          error.body = ''
        }
      "
    />

    <input-section>
      <h1>{{ t('section') }}</h1>

      <div class="grid grid-cols-1 md:grid-cols-5 gap-4">
        <el-form-item :label="t('enName')" prop="enName" class="input-label col-span-2">
          <el-input
            id="en-name"
            v-model="template.enName"
            placeholder=""
            maxlength="255"
            clearable
            required
          >
          </el-input>
        </el-form-item>

        <el-form-item :label="t('arName')" prop="arName" class="input-label col-span-2">
          <el-input
            id="ar-name"
            v-model="template.arName"
            placeholder=""
            maxlength="255"
            clearable
            required
          >
          </el-input>
        </el-form-item>

        <el-form-item :label="t('code')" prop="code" class="input-label">
          <el-input-number
            id="code"
            v-model="template.code"
            :placeholder="t('code')"
            :controls="false"
            clearable
            class="w-full"
          >
          </el-input-number>
        </el-form-item>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
        <!-- Main Category - Read only, preselected as Assets -->
        <el-form-item :label="t('mainCategory')" class="input-label" prop="accountCategory">
          <el-input
            id="account-category"
            :value="t('assets')"
            disabled
            class="w-full"
          />
        </el-form-item>

        <!-- Belongs to Account - Read only text showing Safe or Bank -->
        <el-form-item :label="t('belongsToAccount')" class="input-label" prop="parentAccountID">
          <el-input
            id="parent-account"
            :value="accountType === 'safe' ? t('safe') : t('bank')"
            disabled
            class="w-full"
          />
        </el-form-item>

        <!-- Subcategory - Read only, preselected as Current Asset -->
        <el-form-item :label="t('subcategory')" class="input-label" prop="subcategory">
          <el-input
            id="account-subcategory"
            :value="t('current-assets')"
            disabled
            class="w-full"
          />
        </el-form-item>

        <!-- Balance - Read only, preselected as Debit -->
        <el-form-item :label="t('balance')" class="input-label" prop="balance">
          <el-input
            id="balance"
            :value="t('debit')"
            disabled
            class="w-full"
          />
        </el-form-item>
      </div>

      <div class="grid grid-cols-1 gap-4">
        <el-form-item :label="t('enDescription')" prop="enDescription" class="input-label">
          <el-input
            id="en-description"
            v-model="template.enDescription"
            maxlength="255"
            clearable
          >
          </el-input>
        </el-form-item>
      </div>

      <div class="grid grid-cols-1 gap-4">
        <el-form-item :label="t('arDescription')" prop="arDescription" class="input-label">
          <el-input
            id="ar-description"
            v-model="template.arDescription"
            maxlength="255"
            clearable
          >
          </el-input>
        </el-form-item>
      </div>

      <div class="flex flex-wrap gap-6 mt-4">
        <el-form-item prop="isOnlyForCurrentLocation" class="mb-0">
          <el-checkbox
            id="is-only-for-current-location"
            v-model="template.isOnlyForCurrentLocation"
            :disabled="accountType === 'safe'"
            @change="onOnlyForCurrentLocationChange"
          >
            {{ t('onlyForThisLocation') }}
          </el-checkbox>
        </el-form-item>

        <el-form-item prop="isTransactional" class="mb-0">
          <el-checkbox
            id="is-transactional"
            v-model="template.isTransactional"
            @change="onTransactionalChange"
          >
            {{ t('transactional') }}
          </el-checkbox>
        </el-form-item>

        <el-form-item prop="isCashierRegister" class="mb-0">
          <el-checkbox
            id="is-cashier-register"
            v-model="template.isCashierRegister"
            :disabled="!template.isTransactional"
          >
            {{ t('cashierRegister') }}
          </el-checkbox>
        </el-form-item>
      </div>
    </input-section>
  </el-form>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'

// components
import HorizontalButton from '@/components/HorizontalButton.vue'
import InputSection from '@/components/form/InputSection.vue'
import AlertBadge from '@/components/AlertBadge.vue'

import { ElForm } from 'element-plus'

// models
import { AccountTemplate, accountTemplateI18nMessages } from '@/models/accounting/AccountTemplate'
import { useI18n } from 'vue-i18n'

export default defineComponent({
  name: 'safe-bank-form',

  setup() {
    const { t } = useI18n({
      messages: {
        en: {
          ...accountTemplateI18nMessages.en,
          section: 'Account Info',
          mainCategory: 'Main Category',
          subcategory: 'Subcategory',
          belongsToAccount: 'Belongs to Account',
          onlyForThisLocation: 'Only for this location',
          transactional: 'Transactional',
          cashierRegister: 'Cashier Register',
          assets: 'Assets',
          'current-assets': 'Current Asset',
          safe: 'Safe',
          bank: 'Bank'
        },
        ar: {
          ...accountTemplateI18nMessages.ar,
          section: 'معلومات الحساب',
          mainCategory: 'الفئة الرئيسية',
          subcategory: 'الفئة الفرعية',
          belongsToAccount: 'تابع للحساب',
          onlyForThisLocation: 'للموقع الحالي فقط',
          transactional: 'حساب معاملات',
          cashierRegister: 'حساب صندوق',
          assets: 'الممتلكات',
          'current-assets': 'أصول متداولة',
          safe: 'خزنة',
          bank: 'بنك'
        }
      },
      unescape: 'global'
    })

    return { t }
  },

  components: {
    AlertBadge,
    HorizontalButton,
    InputSection
  },

  props: {
    initTemplate: {
      type: Object as PropType<AccountTemplate>,
      required: true
    },
    accountType: {
      type: String as PropType<'safe' | 'bank'>,
      required: true
    },
    submitAction: {
      type: Function,
      required: true
    },
    successMsg: {
      type: String,
      required: true
    },
    enableClearForm: {
      type: Boolean,
      required: false
    },
    clearFormOnSubmit: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      template: this.initTemplate,
      error: { title: '', body: '' },
      rules: {
        enName: [
          {
            required: true,
            message: this.$t('validation.required'),
            trigger: 'blur'
          },
          {
            max: 255,
            message: this.$t('validation.maxLength', { len: 255 }),
            trigger: 'blur'
          }
        ],
        arName: [
          {
            required: true,
            message: this.$t('validation.required'),
            trigger: 'blur'
          },
          {
            max: 255,
            message: this.$t('validation.maxLength', { len: 255 }),
            trigger: 'blur'
          }
        ]
      }
    }
  },

  mounted() {
    // Set fixed values for safe/bank accounts
    this.template.accountCategory = 'assets' as any
    this.template.accountSubcategory = 'current-assets'
    this.template.balance = 'debit'
    this.template.isTransactional = true

    // For safes: always current location only, transactional, and cashier register
    if (this.accountType === 'safe') {
      this.template.isOnlyForCurrentLocation = true
      this.template.isSharedAccount = false
      this.template.isTransactional = true
      this.template.isCashierRegister = true
    }
  },

  methods: {
    clearForm(formName: string) {
      const form = this.$refs[formName] as typeof ElForm
      form.resetFields()
      // Re-apply fixed values after reset
      this.template.accountCategory = 'assets' as any
      this.template.accountSubcategory = 'current-assets'
      this.template.balance = 'debit'
      this.template.isTransactional = true

      // For safes: always current location only, transactional, and cashier register
      if (this.accountType === 'safe') {
        this.template.isOnlyForCurrentLocation = true
        this.template.isSharedAccount = false
        this.template.isTransactional = true
        this.template.isCashierRegister = true
      }
    },

    async validateSubmit(formName: string) {
      const form = this.$refs[formName] as typeof ElForm
      form?.validate(async (valid: boolean) => {
        if (!valid) {
          document.getElementById('main')?.scrollTo({ top: 0 })
          return false
        }

        const resp = await this.submitAction(this.template)

        if (resp) {
          this.error.title = resp?.title
          this.error.body = resp?.body
          document.getElementById('main')?.scrollTo({ top: 0 })
          return false
        } else {
          if (this.clearFormOnSubmit) {
            this.clearForm(formName)
          }

          this.$alertModal.showSuccess({ title: this.successMsg, body: '' })
          this.$router.push(this.$Route.ACCOUNTING_SAFES_BANKS)
        }
        return true
      })
    },

    onOnlyForCurrentLocationChange(value: boolean) {
      this.template.isSharedAccount = !value
    },

    onTransactionalChange(value: boolean) {
      if (!value && this.template.isCashierRegister) {
        this.template.isCashierRegister = false
      }
    }
  }
})
</script>

<style scoped>
h1,
h2,
h3 {
  @apply text-lg font-bold;
}

form {
  @apply flex flex-col items-center;
}
</style>
